import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

import { formatINR, inrWhole } from "@/lib/utils";
import { buildWaterfall } from "@/lib/costWaterfall";
import type { FlockEconomics } from "@/types/models";

// Cost-driver breakdown for the flock card: a single horizontal stacked bar that
// splits cost into feed / chick / overhead, plus a per-driver row (value + share).
// Decomposition only — the authoritative cost/egg, realized price, margin and
// operating cost live on the card header and grid, so this panel never restates a
// total the rounded rows would have to foot to. Money math is in buildWaterfall.
// See docs/architecture/cost-driver-waterfall.md.

// feed / chick / overhead — stable colours regardless of which drivers are present.
const DRIVER_COLORS: Record<string, string> = {
  Feed: "#2b6553",
  Chick: "#35798c",
  Overhead: "#e0a526",
};

export default function CostWaterfall({ econ }: { econ: FlockEconomics }) {
  const w = buildWaterfall(econ);
  const perEgg = w.mode === "per-egg";
  const fmt = (v: number) => (perEgg ? formatINR(v, 2) : inrWhole(v));
  const unit = perEgg ? "/egg" : "";

  const options: ApexOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit", sparkline: { enabled: true } },
    plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: "100%" } },
    colors: w.drivers.map((d) => DRIVER_COLORS[d.label]),
    dataLabels: {
      enabled: true,
      // Drivers are all strictly positive (builder guarantee); still guard so a
      // value that formats to nothing is skipped rather than labelled "₹0.00".
      formatter: (v: number) => (v > 0 ? fmt(Number(v)) : ""),
      style: { fontSize: "11px", colors: ["#fff"] },
    },
    xaxis: { categories: ["Cost"] },
    legend: { show: false },
    tooltip: { y: { formatter: (v: number) => fmt(v) } },
  };
  const series = w.drivers.map((d) => ({ name: d.label, data: [d.value] }));

  return (
    <div className="mb-4">
      {/* The feed-rate-estimated caveat is already surfaced by the HDP banner and
          the "Feed / kg (est.)" grid tile, so it's not repeated here. */}
      <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
        {perEgg ? "Cost breakdown (per egg)" : "Cost breakdown"}
      </h4>

      {w.drivers.length > 0 ? (
        <>
          <ReactApexChart options={options} series={series} type="bar" height={40} />
          <ul className="mt-3 space-y-1 text-sm">
            {w.drivers.map((d) => (
              <li key={d.key} className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: DRIVER_COLORS[d.label] }} />
                  {d.label}
                  <span className="text-xs text-gray-400 dark:text-gray-500">({d.sharePct}% of cost)</span>
                </span>
                <span className="font-medium text-gray-800 dark:text-white/90">
                  {fmt(d.value)}
                  {unit}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-4 text-center text-xs text-gray-400 dark:border-gray-800 dark:bg-white/[0.02] dark:text-gray-500">
          No cost captured yet for this flock.
        </p>
      )}
    </div>
  );
}
